import React from 'react';
import {
  Box,
  Button,
  HStack,
  Spinner,
  Text,
  VStack,
  Badge,
} from '@chakra-ui/react';
import { TrashIcon } from '@phosphor-icons/react';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import toast from 'react-hot-toast';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { m6_2Endpoints } from '../../../endpoints/m6-2';
import { IManualEntryPreferences } from '../../../models/m6-2';

dayjs.locale('it');

/**
 * Sezione preferenze input manuale nella pagina profilo coach (M6.2 B13).
 * Mostra tutti i record salvati (scope, campi, lastUsedAt, useCount) con
 * possibilità di eliminarli. La modifica avviene tramite il wizard
 * ManualEntryFieldSelector (integrazione futura Sprint 6).
 */
export const ManualEntryPreferencesSection: React.FC = () => {
  const qc = useQueryClient();

  const { data: prefs = [], isLoading, isError, refetch } = useQuery<IManualEntryPreferences[]>({
    queryKey: ['manual-entry-preferences', 'mine'],
    queryFn: async () => {
      const res = await m6_2Endpoints.getMyManualEntryPreferences();
      return res.data;
    },
    staleTime: 2 * 60 * 1000,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => m6_2Endpoints.deleteManualEntryPreferences(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['manual-entry-preferences', 'mine'] });
      toast.success('Preferenza eliminata');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message ?? 'Errore durante l\'eliminazione');
    },
  });

  const scopeLabel = (pref: IManualEntryPreferences): string => {
    if (pref.scopeType === 'team') return `Team · ${pref.scopeId}`;
    if (pref.scopeType === 'individual_athlete') return `Atleta · ${pref.scopeId}`;
    return 'Globale (utente)';
  };

  if (isLoading) {
    return (
      <Box textAlign="center" py={6}>
        <Spinner size="sm" />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box p={4} bg="red.50" borderRadius="md">
        <HStack justifyContent="space-between">
          <Text fontSize="sm" color="red.600">Errore nel caricamento delle preferenze</Text>
          <Button size="xs" variant="ghost" colorPalette="red" onClick={() => refetch()}>
            Riprova
          </Button>
        </HStack>
      </Box>
    );
  }

  return (
    <VStack align="stretch" gap={3}>
      <Box>
        <Text fontSize="md" fontWeight="700" mb={1}>
          Preferenze inserimento manuale
        </Text>
        <Text fontSize="sm" color="gray.500">
          Configurazioni dei campi salvate durante il wizard di inserimento dati GPS.
        </Text>
      </Box>

      {prefs.length === 0 ? (
        <Box bg="gray.50" p={5} borderRadius="md" textAlign="center">
          <Text fontSize="sm" color="gray.400">
            Nessuna preferenza salvata. Le preferenze vengono create automaticamente
            quando si usa il wizard di inserimento manuale.
          </Text>
        </Box>
      ) : (
        <VStack align="stretch" gap={2}>
          {prefs.map((pref) => (
            <Box
              key={pref._id}
              p={4}
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="md"
            >
              <HStack justifyContent="space-between" alignItems="flex-start">
                <Box flex={1}>
                  <HStack gap={2} mb={1} flexWrap="wrap">
                    <Badge colorPalette="blue" size="sm">
                      {pref.scopeType === 'team'
                        ? 'Team'
                        : pref.scopeType === 'individual_athlete'
                        ? 'Atleta individuale'
                        : 'Globale'}
                    </Badge>
                    <Text fontSize="xs" color="gray.600" fontWeight="medium">
                      {scopeLabel(pref)}
                    </Text>
                  </HStack>

                  <Text fontSize="sm" color="gray.700" mb={1}>
                    {pref.fieldsSelected.length} campo/i selezionato/i
                    {pref.fieldsSelected.length > 0 && (
                      <Text as="span" color="gray.400" fontSize="xs" ml={2}>
                        ({pref.fieldsSelected.map((f) => f.canonicalKey).join(', ')})
                      </Text>
                    )}
                  </Text>

                  <HStack gap={4}>
                    {pref.lastUsedAt && (
                      <Text fontSize="xs" color="gray.400">
                        Ultimo utilizzo: {dayjs(pref.lastUsedAt).format('D MMM YYYY')}
                      </Text>
                    )}
                    <Text fontSize="xs" color="gray.400">
                      Utilizzato {pref.useCount ?? 0} volt{(pref.useCount ?? 0) === 1 ? 'a' : 'e'}
                    </Text>
                  </HStack>
                </Box>

                <Button
                  size="xs"
                  variant="ghost"
                  colorPalette="red"
                  onClick={() => deleteMutation.mutate(pref._id)}
                  loading={deleteMutation.isPending && deleteMutation.variables === pref._id}
                  title="Elimina preferenza"
                >
                  <TrashIcon size={14} />
                </Button>
              </HStack>
            </Box>
          ))}
        </VStack>
      )}
    </VStack>
  );
};
